/**
 * The resolved timezone for the current user: user's own timezone (set on the
 * users row) → org's timezone (org_details.timezone_id) → browser zone.
 *
 * Report filter drawers use this as the default timezone instead of the
 * browser's zone, so reports open on the user's/company's working timezone.
 *
 * The fetch is cached at module level so it happens once app-wide: the first
 * component to mount triggers the request, and every subsequent drawer gets
 * the resolved value immediately — no flash of the browser zone, and no
 * duplicate requests when switching between reports.
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client.js'

interface OrgSettingsResponse {
  immediateAvailable: boolean
  timezoneId: string | null
}

// ── Module-level singleton cache ──────────────────────────────────────────
// One fetch for the whole app. The first hook to mount starts it; every other
// hook reads the same promise and its result. This means a report drawer that
// opens after the fetch has already resolved (e.g. the agent navigated from
// one report to another) gets the timezone synchronously — no flash.
let cachedPromise: Promise<string | null> | null = null
let cachedResult: string | null | undefined = undefined // undefined = not yet resolved

function fetchTimezone(): Promise<string | null> {
  if (cachedPromise) return cachedPromise
  cachedPromise = (async () => {
    try {
      const res = await api.get<OrgSettingsResponse>('/agent/org-settings')
      cachedResult = res.timezoneId ?? null
      return cachedResult
    } catch {
      cachedResult = null
      return null
    }
  })()
  return cachedPromise
}

export function useOrgTimezone(): { timezone: string; loading: boolean } {
  const browserZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  )
  const [resolved, setResolved] = useState<string | null | undefined>(cachedResult)
  const [loading, setLoading] = useState(cachedResult === undefined)

  useEffect(() => {
    if (cachedResult !== undefined) {
      // Already resolved by a previous mount — use it immediately.
      setResolved(cachedResult)
      setLoading(false)
      return
    }
    let cancelled = false
    void fetchTimezone().then((tz) => {
      if (!cancelled) {
        setResolved(tz)
        setLoading(false)
      }
    })
    return () => { cancelled = true }
  }, [])

  return { timezone: resolved ?? browserZone, loading }
}
